import React from 'react';

interface ButtonProps {
    id?: string;
    children?: React.ReactNode;
    onClick?: React.MouseEventHandler<HTMLButtonElement>;
    icon?: React.ReactNode;
    trailingIcon?: React.ReactNode;
    className?: string;
    disabled?: boolean;
    variant?: 'primary' | 'secondary' | 'danger' | 'ghost' | 'link';
    /** @deprecated Use variant instead */
    mode?: 'light' | 'dark';
    size?: 'mini' | 'sm' | 'md' | 'lg' | 'icon';
    type?: 'button' | 'submit' | 'reset';
    fullWidth?: boolean;
    ariaLabel?: string;
    ariaControls?: string;
    ariaExpanded?: boolean;
    buttonRef?: React.Ref<HTMLButtonElement>;
}

const sizeClasses: Record<NonNullable<ButtonProps['size']>, string> = {
    mini: 'min-h-8 px-2.5 text-xs',
    sm: 'min-h-9 px-3 text-sm',
    md: 'min-h-10 px-4 text-sm',
    lg: 'min-h-11 px-5 text-base',
    icon: 'h-10 w-10 p-0',
};

const variantClasses: Record<NonNullable<ButtonProps['variant']>, string> = {
    primary: 'border border-btn-primary bg-btn-primary text-btn-primary-text hover:bg-btn-primary-hover shadow-sm',
    secondary: 'border border-btn-secondary-border bg-btn-secondary text-btn-secondary-text hover:bg-btn-secondary-hover',
    danger: 'border border-error/20 bg-error/10 text-error hover:bg-error/15',
    ghost: 'border border-transparent bg-transparent text-content-secondary hover:bg-hover hover:text-content',
    link: 'border border-transparent bg-transparent px-0 text-content-secondary hover:text-content hover:underline shadow-none',
};

const Button: React.FC<ButtonProps> = ({
    id,
    children,
    onClick,
    icon,
    trailingIcon,
    className,
    disabled,
    variant,
    mode,
    size = 'md',
    type = 'button',
    fullWidth = false,
    ariaLabel,
    ariaControls,
    ariaExpanded,
    buttonRef,
}) => {
    const resolvedVariant = variant ?? (mode === 'light' ? 'secondary' : 'primary');
    const baseClasses = 'inline-flex items-center justify-center gap-2 rounded-xl font-medium transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-focus-ring focus:ring-offset-2 focus:ring-offset-surface disabled:opacity-50 disabled:cursor-not-allowed';
    const resolvedSizeClasses = resolvedVariant === 'link' ? 'min-h-0 p-0 text-sm' : sizeClasses[size];
    const buttonClasses = [
        baseClasses,
        variantClasses[resolvedVariant],
        resolvedSizeClasses,
        fullWidth ? 'w-full' : '',
        className,
    ].filter(Boolean).join(' ');

    return (
        <button
            id={id}
            ref={buttonRef}
            type={type}
            onClick={onClick}
            className={buttonClasses}
            disabled={disabled}
            aria-label={ariaLabel}
            aria-controls={ariaControls}
            aria-expanded={ariaExpanded}
        >
            {icon && <span className="flex-shrink-0">{icon}</span>}
            {children && <span>{children}</span>}
            {trailingIcon && <span className="flex-shrink-0">{trailingIcon}</span>}
        </button>
    );
};

export default Button;
